import { useAuth0 } from '@auth0/auth0-react';
import { useIsTelekomProject } from '@core/logic/hooks/useIsTelekomProject';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useSelector } from 'react-redux';
import {
  AssetTypes,
  getAssetsBySma,
  getOrderNumbers,
  getPointElementOrderNumbers,
  getSplineElementOrderNumbers,
} from './api/ordernumbers';
import { getPhoto, getPhotoCount, getPhotoList, getSignedPhoto } from './api/photos';
import {
  getContainableSplineElements,
  getSplineElementContainedAttributes,
} from './api/splineElements';

// Your application is expected to have only one createApi call in it
// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics

export const query = createApi({
  baseQuery: fakeBaseQuery(),
  reducerPath: 'api',
  keepUnusedDataFor: Infinity,
  tagTypes: ['containableElements', 'splineElementContainedAttributes'],
  endpoints: (builder) => ({
    getPhotoList: builder.query({ queryFn: getPhotoList }),
    getPhoto: builder.query({ queryFn: getPhoto }),
    getSignedPhoto: builder.query({ queryFn: getSignedPhoto }),
    getPhotoCount: builder.query({ queryFn: getPhotoCount }),
    getPointElementOrderNumbers: builder.query({ queryFn: getPointElementOrderNumbers }),
    getSplineElementOrderNumbers: builder.query({ queryFn: getSplineElementOrderNumbers }),
    getAssetsBySma: builder.query({ queryFn: getAssetsBySma }),
    getOrderNumbers: builder.query({ queryFn: getOrderNumbers }),
    getContainableSplineElements: builder.query({
      queryFn: getContainableSplineElements,
      providesTags: (_result, _error, arg) => [{ type: 'containableElements', id: arg.elementId }],
    }),
    getSplineElementContainedAttributes: builder.query({
      queryFn: getSplineElementContainedAttributes,
      providesTags: (_result, _error, arg) => [
        { type: 'splineElementContainedAttributes', id: arg.elementId },
      ],
    }),
  }),
});

export const {
  useGetPhotoListQuery,
  useLazyGetPhotoQuery,
  useLazyGetSignedPhotoQuery,
  useGetPhotoCountQuery,
  useGetContainableSplineElementsQuery: useGetContainableSplineElementsQueryFn,
  useGetSplineElementContainedAttributesQuery: useGetSplineElementContainedAttributesQueryFn,
} = query;

const {
  useGetPointElementOrderNumbersQuery: useGetPointElementOrderNumbersQueryFn,
  useGetSplineElementOrderNumbersQuery: useGetSplineElementOrderNumbersQueryFn,
  useGetAssetsBySmaQuery: useGetAssetsBySmaQueryFn,
  useGetOrderNumbersQuery: useGetOrderNumbersQueryFn,
} = query;

export const useGetPointElementOrderNumbersQuery = (
  elementId: string | undefined,
  skip?: boolean,
) => {
  const { isTelekomProject } = useIsTelekomProject();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetPointElementOrderNumbersQueryFn(
    { isTelekomProject, projectId, elementId },
    { skip: !elementId || skip },
  );
};

export const useGetSplineElementOrderNumbersQuery = (
  elementId: string | undefined,
  skip?: boolean,
) => {
  const { isTelekomProject } = useIsTelekomProject();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetSplineElementOrderNumbersQueryFn(
    { isTelekomProject, projectId, elementId },
    { skip: !elementId || skip },
  );
};

export const useGetAssetsBySmaQuery = ({
  orderNumber,
  assetTypes,
}: {
  orderNumber: string | undefined;
  assetTypes: AssetTypes[];
}) => {
  const { isTelekomProject } = useIsTelekomProject();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetAssetsBySmaQueryFn({
    orderNumber,
    projectId,
    assetTypes,
    isTelekomProject,
  });
};

export const useGetSignedPhotoQuery = () => {
  // Signed URLs for photos expire after 15 minutes. Without refetching the expired URL would keep getting used from cache
  const MINUTE = 60_000;
  return useLazyGetSignedPhotoQuery({ pollingInterval: 10 * MINUTE });
};

export const useGetOrderNumbersQuery = (skip?: boolean) => {
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetOrderNumbersQueryFn({ projectId }, { skip: !projectId || skip });
};

export const useGetContainableSplineElementsQuery = (
  elementId: string | undefined,
  skip?: boolean,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetContainableSplineElementsQueryFn(
    { projectId, elementId, getAccessToken: () => getAccessTokenSilently() as Promise<string> },
    { skip: !projectId || skip },
  );
};

export const useGetSplineElementContainedAttributesQuery = (
  elementId: string | undefined,
  skip?: boolean,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  return useGetSplineElementContainedAttributesQueryFn(
    { projectId, elementId, getAccessToken: () => getAccessTokenSilently() as Promise<string> },
    { skip: !projectId || skip },
  );
};
